import { useCallback, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

export type DetailKind = 'metric' | 'hooper' | 'effort';

/**
 * Stato di apertura del "dettaglio" (modal storico metrica/Hooper) derivato
 * al 100% dal query param `?detail=metric|hooper`, invece di un `useState`
 * scollegato dalla history del browser.
 *
 * Bug B4 (#18): aprire il dettaglio non creava alcuna entry di history →
 * il tasto Indietro consumava l'entry reale precedente e usciva dalla pagina
 * team. `openDetail` crea/aggiorna quella entry; `closeDetail` la consuma
 * in modo idempotente, senza doppio-pop nemmeno in caso di doppio click.
 */
export function useDetailParam() {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  // Guard di re-entrancy: `navigate(-1)` è asincrono (`history.go(-1)`), tra
  // la prima chiusura e l'arrivo del `popstate` l'URL live conserva ancora
  // `detail`. Senza guard, un secondo evento di chiusura in quella finestra
  // (doppio-click su X) rilancerebbe un secondo `navigate(-1)` → doppio-pop →
  // uscita dalla pagina team (stessa classe di bug).
  const closingRef = useRef(false);

  const detailKind = (searchParams.get('detail') as DetailKind | null) ?? null;
  const isDetailOpen = detailKind != null;

  // Reset deterministico del guard quando il pop è atterrato (detail → null):
  // non dipende dal fatto che il modale ri-emetta o meno l'evento di chiusura.
  useEffect(() => {
    if (detailKind == null) closingRef.current = false;
  }, [detailKind]);

  // Stale-strip al mount: se il componente monta con `?detail=` già presente
  // (deep-link, refresh, remount da tab-switch) il payload locale del
  // chiamante è sempre `null` → il param è stantio. Va rimosso SENZA navigare
  // (altrimenti un futuro `closeDetail` consumerebbe una history entry che
  // non ci appartiene, uscendo dall'app).
  useEffect(() => {
    if (new URLSearchParams(window.location.search).has('detail')) {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.delete('detail');
          return next;
        },
        { replace: true },
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * Apre il dettaglio: push (nuova entry) se `detail` non è già presente,
   * replace se lo è già (switch A→B senza entry orfane). Il merge da
   * `URLSearchParams` preserva gli altri param (es. `?tab=`).
   */
  const openDetail = useCallback(
    (kind: DetailKind) => {
      const live = new URLSearchParams(window.location.search);
      const alreadyOpen = live.has('detail');
      live.set('detail', kind);
      setSearchParams(live, { replace: alreadyOpen });
    },
    [setSearchParams],
  );

  /**
   * Chiude il dettaglio consumando l'unica entry pushata da `openDetail`.
   * Idempotente: se `detail` non è (più) nell'URL live — perché il Back del
   * browser lo ha già rimosso — è un no-op (nessun secondo `navigate(-1)`).
   *
   * Limite noto (residuo, non risolto): `closingRef` protegge dal doppio-pop
   * quando è `closeDetail` stesso a essere invocato due volte (es. doppio
   * click sulla X). NON copre il caso teorico in cui l'utente preme la X
   * (che chiama `closeDetail` → `navigate(-1)`) e, nella stessa finestra
   * asincrona di pochi millisecondi prima che il `popstate` atterri e resetti
   * `closingRef` (vedi useEffect sopra), preme ANCHE il tasto Indietro nativo
   * del browser: sono due intenti utente distinti che il guard in-app non
   * intercetta (il secondo pop arriva dal browser, non da `closeDetail`).
   * Non riproducibile a mano (finestra troppo stretta); accettato come rischio
   * residuo.
   *
   * Chiarimento: dopo aver chiuso il dettaglio con la X, un successivo Back
   * nativo naviga normalmente alla pagina precedente (es. la lista team) —
   * è il comportamento atteso del browser, NON il bug B4. Il bug B4 era il
   * Back premuto a dettaglio ANCORA APERTO, che usciva dalla pagina team
   * perché l'apertura non creava una entry di history; ora è risolto perché
   * `openDetail` crea quella entry.
   */
  const closeDetail = useCallback(() => {
    if (closingRef.current) return;
    if (new URLSearchParams(window.location.search).has('detail')) {
      closingRef.current = true;
      navigate(-1);
    }
  }, [navigate]);

  return { detailKind, isDetailOpen, openDetail, closeDetail };
}

export default useDetailParam;
